import { AccessGroupsData, ConfigurationAccessGroupsData } from "../api/models/access-groups";
import { RequirementTypesData } from "../api/models/requirement-types";
import { AccessGroupCurrent } from "../routes/Logs/Logs";
import { isVisitorRole } from "./roles";

export type ManualRegistrationScheduleStatus =
  | "allowed"
  | "entrance_not_allowed"
  | "exit_not_allowed";

export interface ManualRegistrationContext {
  status: ManualRegistrationScheduleStatus;
  rule: AccessGroupCurrent | null;
  requirements: RequirementTypesData[];
}

export interface ResolveRegistrationConfigOptions {
  /** Manual guard registration: only visitor access groups. QR: all groups on the key. */
  onlyVisitorGroups?: boolean;
}

const WEEK_DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const parseTimeToMinutes = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const [hours, minutes, seconds = 0] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes + seconds / 60;
};

const getCurrentDayName = (date: Date) => WEEK_DAYS[date.getDay()];

const getCurrentMinutes = (date: Date) =>
  date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;

const isTimeWithinRange = (
  currentMinutes: number,
  startTime: string,
  endTime: string,
): boolean => {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  if (startMinutes === null || endMinutes === null) return false;

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

const isVisitorAccessGroup = (group: AccessGroupsData): boolean => {
  return (group.role_permissions_users ?? []).some((role) =>
    isVisitorRole(role),
  );
};

const mapConfigToRule = (
  group: AccessGroupsData,
  config: ConfigurationAccessGroupsData,
): AccessGroupCurrent => {
  const groupRoleIds =
    group.role_permissions_users?.map((role) => role.id) ?? [];

  return {
    accessGroupId: group.id,
    accessPointId: config.id,
    daysAllowed: config.week_days ?? [],
    antipassback: Number(config.antipassback ?? 0),
    register: !!config.register,
    photographs: config.photos ?? [],
    fromHour: config.start_time,
    toHour: config.end_time,
    exitRestriction:
      config.exit_restriction_from || config.exit_restriction || null,
    exitRestrictionFrom:
      config.exit_restriction_from || config.exit_restriction || null,
    exitRestrictionTo: config.exit_restriction_to || null,
    roles: groupRoleIds,
    printable_pass: !!config.printable_pass,
  };
};

const normalizeRequirementTypes = (
  requirementTypes: AccessGroupsData["requirement_types"] | { data?: RequirementTypesData[] } | null | undefined,
): RequirementTypesData[] => {
  if (!requirementTypes) return [];
  if (Array.isArray(requirementTypes)) return requirementTypes;
  if (Array.isArray((requirementTypes as { data?: RequirementTypesData[] }).data)) {
    return (requirementTypes as { data: RequirementTypesData[] }).data;
  }
  return [];
};

const getTransactionRequirements = (
  group: AccessGroupsData,
): RequirementTypesData[] => {
  // Prefer transaction=true. Also include legacy custom fields that were saved
  // with transaction=false but are not access-key-only (those belong on invitations).
  return normalizeRequirementTypes(group.requirement_types).filter(
    (item) => item?.transaction === true || item?.access_key !== true,
  );
};

const findAllMatchingConfigs = (
  accessGroups: AccessGroupsData[],
  accessPointId: number,
  now: Date,
  onlyVisitorGroups: boolean,
): { group: AccessGroupsData; config: ConfigurationAccessGroupsData }[] => {
  const currentDayName = getCurrentDayName(now);
  const currentMinutes = getCurrentMinutes(now);
  const matches: { group: AccessGroupsData; config: ConfigurationAccessGroupsData }[] =
    [];

  for (const group of accessGroups) {
    if (onlyVisitorGroups && !isVisitorAccessGroup(group)) continue;

    const configs = group.configuration ?? [];
    for (const config of configs) {
      if (config.id !== accessPointId) continue;
      if (!(config.week_days ?? []).includes(currentDayName)) continue;
      if (!isTimeWithinRange(currentMinutes, config.start_time, config.end_time)) {
        continue;
      }

      matches.push({ group, config });
    }
  }

  return matches;
};

const isExitRestrictedAtCurrentTime = (
  config: ConfigurationAccessGroupsData,
  now: Date,
): boolean => {
  const exitFrom = (
    config.exit_restriction_from ||
    config.exit_restriction ||
    ""
  ).trim();
  const exitTo = (config.exit_restriction_to || "").trim();
  if (!exitFrom) return false;

  const currentMinutes = getCurrentMinutes(now);

  // New from/to window: exit is blocked INSIDE the configured range.
  if (exitTo) {
    return isTimeWithinRange(currentMinutes, exitFrom, exitTo);
  }

  // Legacy single exit_restriction value: blocked from that time until schedule end.
  return isTimeWithinRange(currentMinutes, exitFrom, config.end_time);
};

export const resolveRegistrationConfig = (
  accessGroups: AccessGroupsData[] | null,
  accessPointId: number | null,
  gateType: "entrance" | "exit",
  now: Date = new Date(),
  options: ResolveRegistrationConfigOptions = {},
): ManualRegistrationContext => {
  const onlyVisitorGroups = options.onlyVisitorGroups ?? true;

  if (!accessGroups?.length || !accessPointId) {
    return {
      status:
        gateType === "exit" ? "exit_not_allowed" : "entrance_not_allowed",
      rule: null,
      requirements: [],
    };
  }

  const matches = findAllMatchingConfigs(
    accessGroups,
    accessPointId,
    now,
    onlyVisitorGroups,
  );

  if (!matches.length) {
    return {
      status:
        gateType === "exit" ? "exit_not_allowed" : "entrance_not_allowed",
      rule: null,
      requirements: [],
    };
  }

  const primaryMatch = matches[0];
  const rule = mapConfigToRule(primaryMatch.group, primaryMatch.config);
  const requirementsMap = new Map<number, RequirementTypesData>();
  for (const match of matches) {
    for (const requirement of getTransactionRequirements(match.group)) {
      if (typeof requirement?.id === "number") {
        requirementsMap.set(requirement.id, requirement);
      }
    }
  }
  const requirements = Array.from(requirementsMap.values());

  if (gateType === "exit") {
    if (isExitRestrictedAtCurrentTime(primaryMatch.config, now)) {
      return {
        status: "exit_not_allowed",
        rule: null,
        requirements: [],
      };
    }

    return {
      status: "allowed",
      rule,
      requirements,
    };
  }

  return {
    status: "allowed",
    rule,
    requirements,
  };
};

export const resolveManualRegistrationConfig = (
  accessGroups: AccessGroupsData[] | null,
  accessPointId: number | null,
  gateType: "entrance" | "exit",
  now: Date = new Date(),
): ManualRegistrationContext =>
  resolveRegistrationConfig(accessGroups, accessPointId, gateType, now, {
    onlyVisitorGroups: true,
  });
